import {createServerFn} from '@tanstack/react-start';
import {z} from 'zod';
import {authMiddleware} from '@/helpers/authMiddleware';
import {prismaClient} from '@/helpers/prismaClient.server';
import {ProfileExternalId} from '@/apiTypes/profileExternalId';

const LinkInput = z.object({
  integrationKey: z.string().min(1),
  externalId: z.string().min(1),
  displayName: z.string().nullable(),
});

const UnlinkInput = z.object({
  integrationKey: z.string().min(1),
});

/**
 * Creates a `ProfileExternalId` row for the authenticated rider. Throws on
 * uniqueness violations (rider already linked, or that external ID is in use
 * by another rider).
 */
export const linkProfileExternalId = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof LinkInput> => {
    const result = LinkInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}): Promise<ProfileExternalId> => {
    const db = await prismaClient();
    const row = await db.profileExternalId.create({
      data: {
        profileId: context.auth.profile.id,
        integrationKey: data.integrationKey,
        externalId: data.externalId,
        displayName: data.displayName,
      },
    });
    return ProfileExternalId.parse(row);
  });

/**
 * Hard-deletes the caller's `ProfileExternalId` row for the given
 * `integrationKey`. Throws if no row exists.
 */
export const unlinkProfileExternalId = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof UnlinkInput> => {
    const result = UnlinkInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}): Promise<void> => {
    const db = await prismaClient();
    await db.profileExternalId.delete({
      where: {
        profileId_integrationKey: {
          profileId: context.auth.profile.id,
          integrationKey: data.integrationKey,
        },
      },
    });
  });

/**
 * Returns the caller's `ProfileExternalId` rows. Used by the connections
 * dashboard route loader.
 */
export const getProfileExternalIds = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .handler(async ({context}): Promise<ProfileExternalId[]> => {
    const db = await prismaClient();
    const rows = await db.profileExternalId.findMany({
      where: {profileId: context.auth.profile.id},
    });
    return rows.map((row) => ProfileExternalId.parse(row));
  });
